import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, CheckCircle } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import SelectField from '../common/SelectField.js';
import MlLocationChips from './MlLocationChips.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import shipmentQrStatusService from '../../services/shipmentQrStatusService.js';
import routeService from '../../services/RouteService.js';
import { useAuth } from '../../context/AuthContext.js';
import { canAddMlSheetShipments, hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import { getUnremittedPaidShipments, isShipmentDisabledForMl } from '../../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from '../../hooks/useRequirePaidSettlementBeforeMl.js';
import { formatRouteLabel } from '../../utils/routeDisplay.js';
import { formatLocationDropdownLabel } from '../../utils/locationDropdownLabel.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';
import { listIdLinkClass } from '../common/uiClasses.js';

const AgentDispatchToMlSheet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [fromIds, setFromIds] = useState([]);
  const [toIds, setToIds] = useState([]);
  const [matchedRoute, setMatchedRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [mlSheets, setMlSheets] = useState([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [selectedSheetId, setSelectedSheetId] = useState('');
  const [sheetDetail, setSheetDetail] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [serviceNumber, setServiceNumber] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [manualServiceFilter, setManualServiceFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const {
    loaded: mlBlockLoaded,
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user);

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user);
  const shipmentDisabledForMl = (shipment) => isShipmentDisabledForMl(shipment, user, requiresPaidBeforeMl);

  const locationLabel = (loc) =>
    formatLocationDropdownLabel(loc) || loc?.location || loc?.hub_name || '';

  const eligibleToLocations = useMemo(() => {
    if (!hasInternalHubDispatchControls(user)) return locations;
    return toLocationsForMlDispatch(locations, fromIds, { enabled: true });
  }, [locations, fromIds, user]);

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) {
      Alert.error('Dispatch to ML sheet is not available for this role');
      navigate('/ml-sheets');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await mlSheetService.getLocations();
        const rows = res?.data?.data || res?.data || [];
        if (!cancelled) {
          setLocations(Array.isArray(rows) ? rows : []);
          const hub = user?.location_hub_id;
          if (hub != null && hub !== '') {
            setFromIds([Number(hub)]);
          }
        }
      } catch {
        if (!cancelled) Alert.error('Failed to load locations');
      } finally {
        if (!cancelled) setInitLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!fromIds.length || !toIds.length) {
      setMatchedRoute(null);
      setServiceNumber('');
      return undefined;
    }
    let cancelled = false;
    setRouteLoading(true);
    routeService
      .match(fromIds, toIds)
      .then((res) => {
        if (cancelled) return;
        const route = res?.data ?? null;
        setMatchedRoute(route);
        setServiceNumber(route?.route_id ? String(route.route_id) : '');
      })
      .catch(() => {
        if (!cancelled) {
          setMatchedRoute(null);
          setServiceNumber('');
        }
      })
      .finally(() => {
        if (!cancelled) setRouteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fromIds, toIds]);

  const routeStrictMode = Boolean(matchedRoute?.route_id || matchedRoute?.id);

  useEffect(() => {
    if (routeStrictMode) {
      setManualServiceFilter('');
      setVehicleFilter('');
    }
  }, [routeStrictMode]);

  useEffect(() => {
    if (!fromIds.length || !toIds.length) {
      setMlSheets([]);
      setSelectedSheetId('');
      return undefined;
    }
    let cancelled = false;
    setSheetsLoading(true);
    const params = {
      sender_location_hub_id: fromIds[0],
      receiver_location_hub_id: toIds[toIds.length - 1],
    };
    if (routeStrictMode && serviceNumber.trim()) {
      params.service_number = serviceNumber.trim();
    } else {
      if (manualServiceFilter.trim()) {
        params.service_number = manualServiceFilter.trim();
      }
      if (vehicleFilter.trim()) {
        params.vehicle_number = vehicleFilter.trim();
      }
    }
    shipmentQrStatusService
      .listTodayMlSheets(params)
      .then((res) => {
        if (cancelled) return;
        let rows = res?.data || [];
        if (!Array.isArray(rows)) rows = [];
        if (routeStrictMode && serviceNumber.trim()) {
          const svc = serviceNumber.trim().toUpperCase();
          rows = rows.filter(
            (sheet) => String(sheet.service_number || '').trim().toUpperCase() === svc,
          );
        }
        setMlSheets(rows);
        if (rows.length === 1) {
          setSelectedSheetId(rows[0].ml_sheet_id);
        } else {
          setSelectedSheetId((current) =>
            (current && rows.some((r) => r.ml_sheet_id === current) ? current : ''),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setMlSheets([]);
      })
      .finally(() => {
        if (!cancelled) setSheetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fromIds, toIds, serviceNumber, routeStrictMode, manualServiceFilter, vehicleFilter]);

  const serviceOptions = useMemo(() => {
    const values = new Set();
    mlSheets.forEach((sheet) => {
      const text = String(sheet.service_number || '').trim();
      if (text) values.add(text);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [mlSheets]);

  const vehicleOptions = useMemo(() => {
    const values = new Set();
    mlSheets.forEach((sheet) => {
      const text = String(sheet.vehicle_number || '').trim();
      if (text) values.add(text);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [mlSheets]);

  const displayMlSheets = useMemo(() => {
    if (routeStrictMode) return mlSheets;
    let rows = mlSheets;
    const svc = manualServiceFilter.trim().toUpperCase();
    const veh = vehicleFilter.trim().toUpperCase();
    if (svc) {
      rows = rows.filter(
        (sheet) => String(sheet.service_number || '').trim().toUpperCase() === svc,
      );
    }
    if (veh) {
      rows = rows.filter(
        (sheet) => String(sheet.vehicle_number || '').trim().toUpperCase() === veh,
      );
    }
    return rows;
  }, [mlSheets, routeStrictMode, manualServiceFilter, vehicleFilter]);

  const loadShipmentsForSheet = useCallback(async (mlSheetId) => {
    if (!mlSheetId) {
      setSheetDetail(null);
      setShipments([]);
      setSelected([]);
      return;
    }
    setShipmentsLoading(true);
    try {
      const response = await mlSheetService.getPickupAddableShipments(mlSheetId);
      setSheetDetail(response.data?.ml_sheet || null);
      setShipments(response.data?.shipments || []);
      setServiceNumber((prev) => prev || response.data?.service_number || '');
      setSelected([]);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load shipments for this sheet');
      setSheetDetail(null);
      setShipments([]);
    } finally {
      setShipmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShipmentsForSheet(selectedSheetId);
  }, [selectedSheetId, loadShipmentsForSheet]);

  const visibleShipments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return shipments;
    return shipments.filter((shipment) => {
      const hay = [
        shipment.shipment_id,
        shipment.sender_location,
        shipment.receiver_location,
        shipment.sender_contact,
        shipment.receiver_contact,
      ].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [shipments, search]);

  const selectableShipments = useMemo(
    () => visibleShipments.filter((s) => !isShipmentDisabledForMl(s, user, requiresPaidBeforeMl)),
    [visibleShipments, user, requiresPaidBeforeMl],
  );

  const toggle = (shipmentId) => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    const shipment = shipments.find((s) => s.shipment_id === shipmentId);
    if (shipment && shipmentDisabledForMl(shipment)) {
      Alert.error('This Paid shipment has unremitted collections. Settle it first.');
      return;
    }
    setSelected((prev) =>
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId],
    );
  };

  const selectAllVisible = () => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    setSelected(selectableShipments.map((s) => s.shipment_id));
  };

  const addFromLocation = (rawId) => {
    const id = Number(rawId);
    if (!id) return;
    setFromIds((prev) => (prev.some((x) => Number(x) === id) ? prev : [...prev, id]));
  };

  const addToLocation = (rawId) => {
    const id = Number(rawId);
    if (!id) return;
    setToIds((prev) => (prev.some((x) => Number(x) === id) ? prev : [...prev, id]));
  };

  const handleDispatch = async () => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    if (!selectedSheetId) {
      Alert.error('Select today\'s ML sheet');
      return;
    }
    const blockedSelected = requiresPaidBeforeMl
      ? getUnremittedPaidShipments(shipments, selected)
      : [];
    if (blockedSelected.length > 0) {
      Alert.error('Selected Paid shipment(s) have unremitted collections. Settle them first.');
      return;
    }
    if (!selected.length) {
      Alert.error('Select at least one shipment');
      return;
    }
    setSaving(true);
    try {
      await mlSheetService.addPickupShipments(selectedSheetId, selected);
      Alert.success(`${selected.length} shipment(s) dispatched on ${selectedSheetId}`);
      navigate(`/ml-sheets/view/${selectedSheetId}`);
    } catch (err) {
      Alert.error(
        err.response?.data?.error
          || 'Shipment cannot be dispatched on this route and vehicle. Check service and ML sheet.',
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedSheet = displayMlSheets.find((s) => s.ml_sheet_id === selectedSheetId)
    || mlSheets.find((s) => s.ml_sheet_id === selectedSheetId);

  const sheetRouteLocked = Boolean(
    routeStrictMode
    || serviceNumber
    || sheetDetail?.route
    || selectedSheet?.service_number,
  );
  const dispatchDisabled =
    !mlBlockLoaded || mlHardBlocked || !selected.length || !selectedSheetId || saving;

  if (initLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} message="Loading..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto pb-24 md:pb-8">
        <ListPageHeader
          title="Dispatch to ML sheet"
          icon={Truck}
          backTo="/ml-sheets"
          className="mb-4 sm:mb-6"
        />

        <p className="text-sm text-gray-600 mb-4">
          Choose From and To, then pick today&apos;s open ML sheet. When a saved service route
          matches, only that service and matching parcels apply. Without a service route, filter by
          vehicle and ML sheet freely for your lane. Closed and yesterday&apos;s sheets are not listed.
        </p>

        <MlPaidSettlementGate
          mlCreationBlock={mlCreationBlock}
          dues={dues}
          agentDues={agentDues}
          show={showPaidSettlementGate}
          hardBlocked={mlHardBlocked}
          settleOpen={settleOpen}
          setSettleOpen={setSettleOpen}
          onSettleSubmitted={refreshMlSettlement}
          settlementPath={settlementPath}
          allowSettle={!isHubAdmin}
          className="mb-4"
        />

        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-800">1. Route (From → To)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From location</label>
              <SelectField
                variant="form"
                searchable
                searchPlaceholder="Search origin"
                value=""
                onChange={(e) => addFromLocation(e.target.value)}
                className="w-full text-sm"
              >
                <option value="">Add origin hub...</option>
                {locations.map((loc) => {
                  const id = loc.id ?? loc.hub_id;
                  return (
                    <option key={id} value={id}>
                      {locationLabel(loc)}
                    </option>
                  );
                })}
              </SelectField>
              <div className="mt-2">
                <MlLocationChips
                  locations={locations}
                  selectedIds={fromIds}
                  onRemove={(id) => setFromIds((prev) => prev.filter((x) => Number(x) !== Number(id)))}
                  compact
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To location</label>
              <SelectField
                variant="form"
                searchable
                searchPlaceholder="Search destination"
                value=""
                onChange={(e) => addToLocation(e.target.value)}
                className="w-full text-sm"
                disabled={!fromIds.length}
              >
                <option value="">{fromIds.length ? 'Add destination...' : 'Select From first'}</option>
                {eligibleToLocations.map((loc) => {
                  const id = loc.id ?? loc.hub_id;
                  return (
                    <option key={id} value={id}>
                      {locationLabel(loc)}
                    </option>
                  );
                })}
              </SelectField>
              <div className="mt-2">
                <MlLocationChips
                  locations={locations}
                  selectedIds={toIds}
                  onRemove={(id) => setToIds((prev) => prev.filter((x) => Number(x) !== Number(id)))}
                  compact
                />
              </div>
            </div>
          </div>

          {routeLoading && <p className="text-xs text-gray-500">Matching service route...</p>}
          {!routeLoading && fromIds.length > 0 && toIds.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm">
              <span className="text-gray-600">Suggested service: </span>
              <span className="font-semibold text-blue-900">
                {matchedRoute
                  ? formatRouteLabel(matchedRoute)
                  : 'No saved route — pick an ML sheet that matches your lane'}
              </span>
            </div>
          )}
        </div>

        {fromIds.length > 0 && toIds.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 space-y-3">
            <h2 className="text-sm font-semibold text-gray-800">2. Today&apos;s ML sheet</h2>
            {sheetsLoading ? (
              <Loader fullScreen={false} size="small" message="Loading today's sheets..." />
            ) : mlSheets.length === 0 ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {routeStrictMode
                  ? 'No open ML sheet for today on this service. Create today\'s dispatch, then return here.'
                  : 'No open ML sheet for today on this From → To lane. Create a sheet or adjust locations.'}
              </p>
            ) : (
              <>
                {!routeStrictMode && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Filter by service (optional)
                      </label>
                      <SelectField
                        variant="form"
                        searchable
                        searchPlaceholder="Search"
                        value={manualServiceFilter}
                        onChange={(e) => {
                          setManualServiceFilter(e.target.value);
                          setSelectedSheetId('');
                        }}
                        className="w-full text-sm"
                      >
                        <option value="">All services today</option>
                        {serviceOptions.map((svc) => (
                          <option key={svc} value={svc}>{svc}</option>
                        ))}
                      </SelectField>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Filter by vehicle (optional)
                      </label>
                      <SelectField
                        variant="form"
                        searchable
                        searchPlaceholder="Search"
                        value={vehicleFilter}
                        onChange={(e) => {
                          setVehicleFilter(e.target.value);
                          setSelectedSheetId('');
                        }}
                        className="w-full text-sm"
                      >
                        <option value="">All vehicles</option>
                        {vehicleOptions.map((veh) => (
                          <option key={veh} value={veh}>{veh}</option>
                        ))}
                      </SelectField>
                    </div>
                  </div>
                )}
                {displayMlSheets.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    No ML sheet matches the selected service/vehicle filters. Clear filters to see all
                    sheets for this lane.
                  </p>
                ) : (
                <SelectField
                  variant="form"
                  value={selectedSheetId}
                  onChange={(e) => setSelectedSheetId(e.target.value)}
                  className="w-full text-sm"
                >
                  {displayMlSheets.length > 1 && <option value="">Select ML sheet...</option>}
                  {displayMlSheets.map((sheet) => (
                    <option key={sheet.ml_sheet_id} value={sheet.ml_sheet_id}>
                      {sheet.ml_sheet_id}
                      {' · '}
                      {sheet.service_number || 'Custom'}
                      {' · '}
                      {sheet.vehicle_number || 'vehicle'}
                      {' · '}
                      {sheet.shipment_count ?? 0} on sheet
                    </option>
                  ))}
                </SelectField>
                )}
                {selectedSheet && (
                  <p className="text-xs text-gray-600">
                    {selectedSheet.from_location} → {selectedSheet.to_location}
                    {' · Journey '}
                    {selectedSheet.journey_date || 'today'}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {selectedSheetId && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-semibold text-gray-800">3. Select shipments</h2>
              {selectableShipments.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={selectAllVisible}
                >
                  Select all ({selectableShipments.length})
                </button>
              )}
            </div>
            {sheetRouteLocked ? (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Only parcels on service{' '}
                <strong>
                  {serviceNumber
                    || selectedSheet?.service_number
                    || formatRouteLabel(sheetDetail?.route)}
                </strong>{' '}
                are listed. Others cannot be dispatched on this route and vehicle.
              </div>
            ) : (
              <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                No service route on this sheet — shipments are filtered by the sheet&apos;s From/To
                locations only.
              </div>
            )}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search GC, location, mobile"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl mb-3"
            />
            {shipmentsLoading ? (
              <Loader fullScreen={false} size="small" message="Loading shipments..." />
            ) : visibleShipments.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No booked shipments on this route to dispatch.</p>
            ) : (
              <ul className="divide-y border border-gray-100 rounded-lg max-h-80 overflow-y-auto">
                {visibleShipments.map((shipment) => {
                  const checked = selected.includes(shipment.shipment_id);
                  const paidBlocked = shipmentDisabledForMl(shipment);
                  return (
                    <li key={shipment.shipment_id}>
                      <label
                        className={`flex items-start gap-3 p-3 ${
                          paidBlocked || mlHardBlocked ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!mlBlockLoaded || mlHardBlocked || paidBlocked}
                          onChange={() => toggle(shipment.shipment_id)}
                          className="mt-1 w-4 h-4 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className={listIdLinkClass}>{shipment.shipment_id}</p>
                          <p className="text-sm text-gray-700">
                            {shipment.sender_location} → {shipment.receiver_location}
                          </p>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="hidden md:block">
          <Button
            icon={CheckCircle}
            loading={saving}
            disabled={dispatchDisabled}
            onClick={handleDispatch}
          >
            Dispatch selected ({selected.length})
          </Button>
        </div>

        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <Button
            icon={CheckCircle}
            loading={saving}
            disabled={dispatchDisabled}
            onClick={handleDispatch}
            className="w-full"
          >
            Dispatch selected ({selected.length})
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AgentDispatchToMlSheet;
